
import { computed, defineComponent, getCurrentInstance, onMounted, onUnmounted, PropType, ref, watch } from 'vue'
import NotificationPhone from '@/modules/marketplace/components/billing/NotificationPhone.vue'
import { useProject } from '@/hooks/useProject'
import { useBillingStore } from '@/store/stores/billing'
import api from '@root/src/api/api'
import config from '@root/config'
import { fetchReworkAuthStatusByConnectorId } from '@/helpers/channels/fetchReworkAuthStatus'
import {
    isAuthStatusLoading,
    isChannelAuthorized,
    shouldUseReworkAuthStatus,
} from '@/helpers/channels/channelAuthStatus'

export default defineComponent({
    name: 'SubscriptionOverview',
    components: { NotificationPhone },
    props: {
        screen: { type: Object as PropType<any>, required: true },
        multiRows: { type: Array as PropType<any[]>, default: () => [] },
        addonRows: { type: Array as PropType<any[]>, default: () => [] },
        channelSlots: { type: Array as PropType<any[]>, default: () => [] },
        currentPeriodMonths: { type: Number, default: 0 },
        canUpgrade: { type: Boolean, default: false },
        refreshing: { type: Boolean, default: false },
        isPaymentHighlighted: { type: Function as PropType<(id?: number | null) => boolean>, required: true },
        setPaymentHighlightRowRef: {
            type: Function as PropType<(el: Element | { $el?: Element } | null, id?: number | null) => void>,
            default: () => {},
        },
        cancellingPendingPaymentId: { type: Number as PropType<number | null>, default: null },
    },
    setup(props) {
        const { project } = useProject()
        const billingStore = useBillingStore()
        const previousTariff = computed(() => billingStore.getPreviousTariff(project.value?.id))
        const multiSubscription = computed(() => props.screen?.multiSubscription || null)
        const scheduledChange = computed(() => multiSubscription.value?.scheduledChanges || null)
        const scheduledPaymentStatus = computed(() =>
            String(scheduledChange.value?.paymentStatus || '').toLowerCase()
        )
        const isScheduledCreated = computed(() => scheduledPaymentStatus.value === 'created')
        const isScheduledPending = computed(() => scheduledPaymentStatus.value === 'pending')
        const isScheduledPaid = computed(() => scheduledPaymentStatus.value === 'paid')
        const isScheduledFailed = computed(() => scheduledPaymentStatus.value === 'failed')
        const showScheduledPayActions = computed(() => isScheduledCreated.value || isScheduledFailed.value)
        const scheduledPaymentUrl = computed(() => {
            const change = scheduledChange.value
            if (!change) return ''
            const direct = typeof change.paymentUrl === 'string' ? change.paymentUrl.trim() : ''
            if (direct) return direct
            const payloadId = Number(change.paymentPayloadId)
            const projectId = project.value?.id
            if (!Number.isFinite(payloadId) || payloadId <= 0 || !projectId) return ''
            const origin = String(config.frontendEndpoint || window.location.origin).replace(/\/$/, '')
            return `${origin}/project/${projectId}/payment/${payloadId}`
        })
        const scheduledChangeTitle = computed(() => {
            if (!scheduledChange.value) return ''
            if (isScheduledFailed.value) return 'Не удалось оплатить переход'
            if (isScheduledPending.value) return 'Платёж ещё обрабатывается'
            if (isScheduledPaid.value) return `С ${formatScheduledChangeDate(scheduledChange.value.activeFrom)} тариф будет изменён`
            return 'Нужна оплата отложенного перехода'
        })
        const scheduledChangeBadge = computed(() => {
            if (isScheduledCreated.value) return 'Не оплачена'
            if (isScheduledPending.value) return 'В обработке'
            if (isScheduledPaid.value) return 'Оплачена'
            if (isScheduledFailed.value) return 'Ошибка оплаты'
            return ''
        })
        const scheduledChangeStatusText = computed(() => {
            if (!scheduledChange.value) return ''
            const dateLabel = formatScheduledChangeDate(scheduledChange.value.activeFrom)
            if (isScheduledCreated.value) {
                return `С ${dateLabel} будет подключён новый тариф. Оплатите сейчас, чтобы переход сохранился.`
            }
            if (isScheduledPending.value) {
                return `С ${dateLabel} будет подключён новый тариф. Ожидаем поступления платежа.`
            }
            if (isScheduledPaid.value) {
                return 'До этой даты продолжит работать текущий тариф.'
            }
            if (isScheduledFailed.value) {
                return `С ${dateLabel} должен подключиться новый тариф. Попробуйте оплатить ещё раз.`
            }
            return `С ${dateLabel} тариф будет изменён.`
        })
        const scheduledChangeIcon = computed(() => {
            if (isScheduledPaid.value) return 'mdi-check-circle-outline'
            if (isScheduledPending.value) return 'mdi-clock-outline'
            if (isScheduledFailed.value) return 'mdi-alert-circle-outline'
            return 'mdi-information-outline'
        })
        const scheduledChangeIconColor = computed(() => {
            if (isScheduledPaid.value) return '#228B46'
            if (isScheduledPending.value) return '#525AD5'
            if (isScheduledFailed.value) return '#E8183C'
            return '#B87500'
        })
        const scheduledChangeToneClass = computed(() => {
            if (isScheduledPaid.value) return 'scheduled-change--paid'
            if (isScheduledPending.value) return 'scheduled-change--pending'
            if (isScheduledFailed.value) return 'scheduled-change--failed'
            return 'scheduled-change--created'
        })
        const billingEnabled = computed(() => Boolean(project.value?.billingEnabled))
        const isUnpaidPlan = computed(() => !multiSubscription.value && billingEnabled.value)
        const hasBlockedPreviousTariff = computed(() =>
            Boolean(isUnpaidPlan.value && previousTariff.value?.integrationId)
        )
        const blockedPlanName = computed(() => previousTariff.value?.name || 'Тариф')
        const blockedAtLabel = computed(() => {
            const raw = previousTariff.value?.uninstalledAt
            if (!raw) return ''
            const date = new Date(raw)
            if (Number.isNaN(date.getTime())) return ''
            return date.toLocaleDateString('ru-RU')
        })
        const blockedPeriodLabel = computed(() => {
            const months = Number(previousTariff.value?.periodInMonths)
            if (!months) return '—'
            const mod100 = months % 100
            const mod10 = months % 10
            let word = 'месяцев'
            if (mod100 < 11 || mod100 > 14) {
                if (mod10 === 1) word = 'месяц'
                else if (mod10 >= 2 && mod10 <= 4) word = 'месяца'
            }
            return `${months} ${word}`
        })
        const showPlanCard = computed(() => Boolean(multiSubscription.value || isUnpaidPlan.value))
        const tariffRow = computed(() =>
            (multiSubscription.value?.rows || []).find((row: any) => row.kind === 'tariff' || row.roleLabel === 'Тариф')
        )
        const planName = computed(() => tariffRow.value?.name || 'Тариф')
        const planInitials = computed(() => initialsFromPlanName(planName.value))
        const isTariffRow = (row: any) => row?.kind === 'tariff' || row?.roleLabel === 'Тариф'
        const isModuleRow = (row: any) =>
            row?.kind === 'module' || /^модуль$/i.test(String(row?.roleLabel || ''))
        const isChannelRow = (row: any) => Boolean(row) && !isTariffRow(row) && !isModuleRow(row)
        const compositionBadgeLabel = (row: any) => {
            if (isTariffRow(row)) return 'Текущий тариф'
            if (row?.inTariff) return 'Входит в тариф'
            if (isModuleRow(row)) return 'Модуль'
            return 'Доп. канал'
        }
        const compositionBadgeTone = (row: any) => {
            if (isTariffRow(row)) return 'neutral'
            if (row?.inTariff) return 'success'
            return 'indigo'
        }
        const endDate = computed(() => parseDate(multiSubscription.value?.periodTo))
        const isExpired = computed(() => Boolean(endDate.value && endDate.value.getTime() < startOfToday().getTime()))
        const shortPaidUntil = computed(() => endDate.value ? formatDate(endDate.value) : 'окончания оплаченного периода')
        const compositionMetaText = (row: any) => {
            if (isTariffRow(row)) {
                return endDate.value ? `Тариф. Оплачен до ${shortPaidUntil.value}.` : 'Тариф'
            }
            return row?.roleLabel || ''
        }
        const paidUntilTitle = computed(() =>
            isExpired.value ? `Подписка была оплачена до ${shortPaidUntil.value}` : `Подписка оплачена до ${shortPaidUntil.value}`
        )
        const paymentProvider = computed(() =>
            String(multiSubscription.value?.paymentMethod?.provider || '').toLowerCase()
        )
        const isInvoicePaymentMethod = computed(() => paymentProvider.value === 'invoice')
        const INVOICE_AUTO_RENEWAL_HINT =
            'Автопродление недоступно при оплате по счету. Чтобы включить его, оплатите подписку картой.'
        const isMainAutoRenewalEnableBlocked = computed(() => {
            if (!multiSubscription.value || multiSubscription.value.isAutoRenewalEnabled) return false
            return isInvoicePaymentMethod.value
        })
        const mainAutoRenewalDisabledReason = computed(() =>
            isMainAutoRenewalEnableBlocked.value ? INVOICE_AUTO_RENEWAL_HINT : ''
        )
        const paymentMethodLabel = computed(() => {
            const paymentMethod = multiSubscription.value?.paymentMethod
            const provider = paymentProvider.value
            if (!paymentMethod || !provider) return 'Появится после оплаты'
            if (provider === 'invoice') return 'Через счет'
            if (!multiSubscription.value?.isAutoRenewalEnabled) return '—'
            if (provider === 'yookassa') {
                const last4 = String(paymentMethod.last4 || '').replace(/\D/g, '').slice(-4)
                return last4 ? `Карта •••• ${last4}` : 'По карте'
            }
            return 'Появится после оплаты'
        })
        const includedChannelsCount = computed(() =>
            (multiSubscription.value?.rows || []).filter((row: any) => isChannelRow(row) && row.inTariff).length
        )
        const extraChannelsCount = computed(() =>
            (multiSubscription.value?.rows || []).filter((row: any) => isChannelRow(row) && !row.inTariff).length
        )
        const planDescription = computed(() => {
            const included = includedChannelsCount.value
            const extra = extraChannelsCount.value
            const includedText = included ? `${included} ${channelWord(included)} ${included === 1 ? 'входит' : 'входят'} в тариф.` : 'Каналы в составе тарифа отображены ниже.'
            const extraText = extra ? ` Дополнительные каналы (${extra}) продлеваются в общей корзине.` : ''
            return `${includedText}${extraText}`
        })
        // Срок текущего оплаченного периода с бэкенда; currentPeriodMonths — период подписки
        // из текущего тарифа биллинга, если бэкенд срок ещё не отдаёт.
        const periodLabel = computed(() => {
            const months =
                Number(multiSubscription.value?.periodMonths) ||
                props.currentPeriodMonths ||
                monthsBetween(multiSubscription.value?.periodFrom, multiSubscription.value?.periodTo)
            return months > 0 ? `${months} ${monthWord(months)}` : 'Текущий период'
        })

        const iconMap: Record<string, string> = {
            tariff: require('@/assets/icons/tariff-briefcase.svg'),
            whatsapp: require('@/assets/icons/whatsApp-white.svg'),
            telegram: require('@/assets/icons/telegram-white.svg'),
            max: require('@/assets/icons/max-white.svg'),
            sms: require('@/assets/img/messenger-logo/sms-logo.png'),
        }

        const iconFor = (kind?: string) => iconMap[String(kind || 'tariff')] || iconMap.tariff
        const formatRub = (value?: number) => `${new Intl.NumberFormat('ru-RU').format(Number(value || 0))} ₽`
        const formatMonthly = (value?: number) => `${formatRub(value)} / мес.`
        // Сумма следующего списания; прочерк, если биллинг не смог её посчитать.
        const renewalPriceLabel = computed(() => {
            const price = multiSubscription.value?.renewalPriceRub
            return price == null ? '—' : formatRub(price)
        })
        const compactPeriod = (value?: string) => String(value || '—').replace(/\s*\([^)]*\)\s*$/, '') || '—'
        const isConnectable = (row: any) => !row?.pendingPayment && /свободный канал/i.test(String(row?.name || ''))
        const isDetachable = (row: any) =>
            !row?.pendingPayment && row?.kind !== 'tariff' && !row?.inTariff && Number(row?.subscriptionId || 0) > 0
        const isMaxBusinessSlotRow = (row: any) => {
            if (!row || row.kind === 'tariff') return false
            if (row.slotType === 'maxSlot') return true
            const slots = props.channelSlots || []
            const slot =
                (row.slotId
                    ? slots.find((item: any) => String(item?.id) === String(row.slotId))
                    : undefined) ||
                (typeof row.subscriptionId === 'number'
                    ? slots.find((item: any) => Number(item?.subscriptionId) === Number(row.subscriptionId))
                    : undefined)
            return slot?.type === 'maxSlot'
        }
        const isAddonProcessing = (item: any) => Boolean(
            item?.paymentStatus?.tone === 'processing' ||
            item?.row?.pendingPayment ||
            item?.addon?.pendingPayment
        )
        const paymentIdOf = (item: any): number | null => {
            const raw = item?.paymentPayloadId ?? item?.row?.paymentPayloadId
            const value = Number(raw)
            return Number.isFinite(value) && value > 0 ? value : null
        }
        const isCancellingAddonPayment = (item: any) => {
            const id = paymentIdOf(item)
            return id != null && Number(props.cancellingPendingPaymentId) === id
        }
        const isRowHighlighted = (item: any) => props.isPaymentHighlighted(paymentIdOf(item))
        const isCompositionListHighlighted = computed(() => {
            const rows = props.multiRows || []
            if (!rows.length) return false
            return rows.every((item: any) => props.isPaymentHighlighted(paymentIdOf(item)))
        })
        const setCompositionListHighlightRef = (el: Element | { $el?: Element } | null) => {
            if (!isCompositionListHighlighted.value) return
            const first = (props.multiRows || [])[0]
            props.setPaymentHighlightRowRef(el, paymentIdOf(first))
        }
        const setCompositionRowHighlightRef = (el: Element | { $el?: Element } | null, item: any) => {
            if (isCompositionListHighlighted.value) return
            props.setPaymentHighlightRowRef(el, paymentIdOf(item))
        }

        const root = getCurrentInstance()?.proxy as { $root?: { $on: Function; $off: Function } } | undefined
        const authStatusByConnectorId = ref<Record<string, boolean | null>>({})
        const authLoadingByConnectorId = ref<Record<string, boolean>>({})
        const authReloadQueuedByConnectorId = ref<Record<string, boolean>>({})
        const authReloadTimers: Record<string, ReturnType<typeof setTimeout>> = {}

        const projectConnectors = computed(() => (project.value?.connectors || []) as any[])
        const connectorIdOf = (connector: any) => String(connector?.connectorId || connector?.id || '')
        const slottedConnectorIds = computed(() =>
            (props.channelSlots || [])
                .map((slot: any) => slot?.channelConnectorId)
                .filter(Boolean)
                .map(String)
        )
        const slottedAliveKey = computed(() =>
            slottedConnectorIds.value
                .map(id => {
                    const connector = projectConnectors.value.find(
                        (item: any) => connectorIdOf(item) === id
                    )
                    return `${id}:${connector?.alive === true ? 1 : 0}`
                })
                .join(',')
        )

        const resolveSlotForRow = (row: any) => {
            const slots = props.channelSlots || []
            if (row?.slotId) {
                const byId = slots.find((slot: any) => String(slot.id) === String(row.slotId))
                if (byId) return byId
            }
            if (typeof row?.subscriptionId === 'number') {
                return slots.find((slot: any) => Number(slot.subscriptionId) === Number(row.subscriptionId))
            }
            return null
        }

        const resolveConnectorForRow = (row: any) => {
            const slot = resolveSlotForRow(row)
            const connectorId = slot?.channelConnectorId
            if (!connectorId) return null
            return (
                projectConnectors.value.find(
                    (connector: any) => String(connector.connectorId || connector.id) === String(connectorId)
                ) || null
            )
        }

        const addonChannelStatus = (item: any): 'trial' | 'active' | 'loading' | 'inactive' | 'processing' => {
            if (item?.addon?.isTrial === true) return 'trial'
            const row = item?.row
            const isProcessing = isAddonProcessing(item)
            if (isModuleRow(row)) return isProcessing ? 'processing' : 'active'
            if (!row) return 'inactive'
            const slot = resolveSlotForRow(row)
            const connectorId = slot?.channelConnectorId
            if (!connectorId) return 'inactive'
            const connector = resolveConnectorForRow(row)
            if (!connector) return 'loading'
            const authItem = {
                ...connector,
                connectorId: connector.connectorId || connector.id,
                providerType: connector.providerType,
                isActive: connector.isActive !== false,
                alive: connector.alive,
            }
            if (isAuthStatusLoading(authItem, authLoadingByConnectorId.value)) return 'loading'
            if (
                isChannelAuthorized(authItem, {
                    statusByConnectorId: authStatusByConnectorId.value,
                    loadingByConnectorId: authLoadingByConnectorId.value,
                })
            ) {
                return 'active'
            }
            return 'inactive'
        }

        const addonChannelStatusLabel = (item: any) => {
            const status = addonChannelStatus(item)
            if (status === 'trial') return 'Пробный'
            if (status === 'active') return 'Активен'
            if (status === 'loading') return 'Загрузка...'
            if (status === 'processing') return 'Оплата в обработке'
            return 'Не активен'
        }

        const addonChannelStatusClass = (item: any) => {
            const status = addonChannelStatus(item)
            if (status === 'active' || status === 'trial') return 'status-badge--channel-active'
            if (status === 'loading' || status === 'processing') return 'status-badge--channel-loading'
            return 'status-badge--channel-inactive'
        }

        const setAuthLoading = (id: string, value: boolean) => {
            authLoadingByConnectorId.value = { ...authLoadingByConnectorId.value, [id]: value }
        }
        const setAuthStatus = (id: string, value: boolean | null) => {
            if (authReloadQueuedByConnectorId.value[id]) return
            authStatusByConnectorId.value = { ...authStatusByConnectorId.value, [id]: value }
        }
        const invalidateAuthStatus = (connectorId: string) => {
            if (authStatusByConnectorId.value[connectorId] === undefined) return
            const next = { ...authStatusByConnectorId.value }
            delete next[connectorId]
            authStatusByConnectorId.value = next
        }

        const requestReworkAuth = async (connectorId: string, { skipIfCached = false } = {}) => {
            if (!connectorId) return
            if (skipIfCached && authStatusByConnectorId.value[connectorId] !== undefined) return
            if (authLoadingByConnectorId.value[connectorId]) {
                authReloadQueuedByConnectorId.value = {
                    ...authReloadQueuedByConnectorId.value,
                    [connectorId]: true,
                }
                return
            }
            await fetchReworkAuthStatusByConnectorId(connectorId, {
                apiClient: api as any,
                isLoading: id => Boolean(authLoadingByConnectorId.value[id]),
                setLoading: setAuthLoading,
                setStatus: setAuthStatus,
            })
            if (authReloadQueuedByConnectorId.value[connectorId]) {
                const queued = { ...authReloadQueuedByConnectorId.value }
                delete queued[connectorId]
                authReloadQueuedByConnectorId.value = queued
                await requestReworkAuth(connectorId)
            }
        }

        const reloadReworkAuthStatus = (connectorId: string) => {
            if (!connectorId) return
            invalidateAuthStatus(connectorId)
            void requestReworkAuth(connectorId)
        }

        const scheduleReloadAuthStatus = (connectorId: string) => {
            if (!connectorId) return
            if (authReloadTimers[connectorId]) clearTimeout(authReloadTimers[connectorId])
            authReloadTimers[connectorId] = setTimeout(() => {
                delete authReloadTimers[connectorId]
                reloadReworkAuthStatus(connectorId)
            }, 400)
        }

        const isSlottedAddonConnector = (connectorId: string) =>
            slottedConnectorIds.value.includes(String(connectorId))

        const prefetchAddonAuthStatuses = () => {
            ;(props.addonRows || []).forEach((item: any) => {
                const connector = resolveConnectorForRow(item?.row)
                if (!connector || !shouldUseReworkAuthStatus(connector)) return
                const connectorId = connectorIdOf(connector)
                if (!connectorId) return
                void requestReworkAuth(connectorId, { skipIfCached: true })
            })
        }

        const handleReloadAuthStatus = (payload: string | { connectorId?: string }) => {
            const connectorId = typeof payload === 'string' ? payload : payload?.connectorId
            if (!connectorId || !isSlottedAddonConnector(connectorId)) return
            invalidateAuthStatus(connectorId)
            scheduleReloadAuthStatus(connectorId)
        }

        const reloadAllSlottedAuthStatuses = () => {
            slottedConnectorIds.value.forEach(connectorId => {
                const connector = projectConnectors.value.find(
                    (item: any) => connectorIdOf(item) === connectorId
                )
                if (!connector || !shouldUseReworkAuthStatus(connector)) return
                invalidateAuthStatus(connectorId)
                scheduleReloadAuthStatus(connectorId)
            })
        }

        const parseAliveKey = (key: string) => {
            const map: Record<string, string> = {}
            String(key || '')
                .split(',')
                .filter(Boolean)
                .forEach(part => {
                    const idx = part.lastIndexOf(':')
                    if (idx < 0) return
                    map[part.slice(0, idx)] = part.slice(idx + 1)
                })
            return map
        }

        watch(
            () => [
                (props.addonRows || []).map((item: any) => item?.row?.slotId || item?.row?.subscriptionId).join(','),
                (props.channelSlots || []).map((slot: any) => slot?.channelConnectorId).join(','),
                projectConnectors.value.map((connector: any) => connectorIdOf(connector)).join(','),
            ],
            prefetchAddonAuthStatuses,
            { immediate: true }
        )

        watch(slottedAliveKey, (next, prev) => {
            if (prev == null) return
            const prevMap = parseAliveKey(prev)
            const nextMap = parseAliveKey(next)
            Object.keys(nextMap).forEach(connectorId => {
                if (prevMap[connectorId] === nextMap[connectorId]) return
                const connector = projectConnectors.value.find(
                    (item: any) => connectorIdOf(item) === connectorId
                )
                if (connector && !shouldUseReworkAuthStatus(connector)) return
                invalidateAuthStatus(connectorId)
                scheduleReloadAuthStatus(connectorId)
            })
        })

        onMounted(() => {
            root?.$root?.$on('reload-channel-auth-status', handleReloadAuthStatus)
            root?.$root?.$on('reload-channel-slots', reloadAllSlottedAuthStatuses)
        })
        onUnmounted(() => {
            root?.$root?.$off('reload-channel-auth-status', handleReloadAuthStatus)
            root?.$root?.$off('reload-channel-slots', reloadAllSlottedAuthStatuses)
            Object.values(authReloadTimers).forEach(timer => clearTimeout(timer))
        })

        return {
            multiSubscription,
            scheduledChange,
            scheduledChangeTitle,
            scheduledChangeBadge,
            scheduledChangeStatusText,
            scheduledChangeIcon,
            scheduledChangeIconColor,
            scheduledChangeToneClass,
            scheduledPaymentUrl,
            showScheduledPayActions,
            isScheduledPending,
            isUnpaidPlan,
            hasBlockedPreviousTariff,
            blockedPlanName,
            blockedAtLabel,
            blockedPeriodLabel,
            showPlanCard,
            planName,
            planInitials,
            isTariffRow,
            compositionMetaText,
            compositionBadgeLabel,
            compositionBadgeTone,
            isExpired,
            shortPaidUntil,
            paidUntilTitle,
            paymentMethodLabel,
            isMainAutoRenewalEnableBlocked,
            mainAutoRenewalDisabledReason,
            planDescription,
            periodLabel,
            renewalPriceLabel,
            iconFor,
            formatRub,
            formatMonthly,
            compactPeriod,
            isConnectable,
            isDetachable,
            isMaxBusinessSlotRow,
            paymentIdOf,
            isRowHighlighted,
            isCompositionListHighlighted,
            setCompositionListHighlightRef,
            setCompositionRowHighlightRef,
            isAddonProcessing,
            isCancellingAddonPayment,
            addonChannelStatusLabel,
            addonChannelStatusClass,
            formatMonths,
        }
    },
})

function parseDate(value?: string): Date | null {
    if (!value) return null
    const raw = String(value).trim()
    const ymd = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/)
    if (ymd) {
        const date = new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]))
        return Number.isNaN(date.getTime()) ? null : date
    }
    const date = new Date(raw)
    return Number.isNaN(date.getTime()) ? null : date
}

function startOfToday(): Date {
    const now = new Date()
    return new Date(now.getFullYear(), now.getMonth(), now.getDate())
}

function initialsFromPlanName(name?: string): string {
    const parts = String(name || '')
        .trim()
        .split(/[\s\-–—_/]+/)
        .filter(Boolean)
    if (!parts.length) return 'DH'
    if (parts.length === 1) {
        return parts[0].slice(0, Math.min(2, parts[0].length)).toUpperCase()
    }
    return parts.slice(0, 2).map(word => word.charAt(0).toUpperCase()).join('')
}

function formatDate(date: Date): string {
    return date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit' })
}

function formatScheduledChangeDate(value?: string): string {
    const date = parseDate(value)
    if (!date) return 'указанной даты'
    date.setDate(date.getDate() + 1)
    return date
        .toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })
        .replace(/\s?г\.$/, '')
}

function monthsBetween(from?: string, to?: string): number {
    const start = parseDate(from)
    const end = parseDate(to)
    if (!start || !end) return 0
    const months = (end.getFullYear() - start.getFullYear()) * 12 + end.getMonth() - start.getMonth()
    return Math.max(0, months)
}

function monthWord(value: number): string {
    const mod10 = value % 10
    const mod100 = value % 100
    if (mod10 === 1 && mod100 !== 11) return 'месяц'
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'месяца'
    return 'месяцев'
}

function channelWord(value: number): string {
    const mod10 = value % 10
    const mod100 = value % 100
    if (mod10 === 1 && mod100 !== 11) return 'канал'
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'канала'
    return 'каналов'
}

function formatMonths(value?: string): string {
    const months = Number(value || 0)
    return months > 0 ? `${months} ${monthWord(months)}` : 'текущий период'
}
